import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Gently-native invoice creation from shipped sales orders.
 *
 * `createFromSalesOrders` idempotently materializes an open AR invoice (plus
 * line items copied from the sales order lines) per sales order; orders that
 * already have an invoice return the existing one with `created: false`. The
 * output rows are shaped to feed straight into the bulk invoice send dialog.
 */
export const CreateInvoicesFromSalesOrdersInputSchema = z.object({
  salesOrderIds: z.array(z.uuid()).min(1).max(100),
});

export const CreatedInvoiceFromSalesOrderSchema = z.object({
  salesOrderId: z.uuid(),
  invoiceId: z.uuid(),
  invoiceNumber: z.string().nullable(),
  customerId: z.uuid(),
  customerName: z.string().nullable(),
  /** False when the sales order already had an invoice (idempotent skip). */
  created: z.boolean(),
});

export const CreateInvoicesFromSalesOrdersResponseSchema = z.object({
  invoices: z.array(CreatedInvoiceFromSalesOrderSchema),
});

export const arInvoiceFromSalesOrderContracts = {
  createFromSalesOrders: oc
    .input(CreateInvoicesFromSalesOrdersInputSchema)
    .output(CreateInvoicesFromSalesOrdersResponseSchema)
    .route({
      method: "POST",
      summary: "Create invoices from shipped sales orders",
      description:
        "Creates an open AR invoice with lines copied from each selected sales order (up to 100). Orders that already have an invoice return the existing one.",
    }),
} as const;

export type CreateInvoicesFromSalesOrdersInput = z.infer<
  typeof CreateInvoicesFromSalesOrdersInputSchema
>;
export type CreatedInvoiceFromSalesOrderDto = z.infer<
  typeof CreatedInvoiceFromSalesOrderSchema
>;
export type CreateInvoicesFromSalesOrdersResponse = z.infer<
  typeof CreateInvoicesFromSalesOrdersResponseSchema
>;
